/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EmployeeContext } from '../contexts';
import {
  EmpDepartment,
  Employee,
  IDelegateOption,
  RoundHistory
} from '../../config/interfaces';

interface IGetSubjectsToRoundPayload {
  hcaid: string;
  type?: string;
}

interface IGetEmployeesToRoundPayload extends IGetSubjectsToRoundPayload {
  directReportsOnly: boolean;
}

/**
 * Redux slice for managing employee-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to employee management, department selection, employee search,
 * employee notes, delegation, and support/validation employee lists, including:
 * - Setting and clearing departments and selected departments
 * - Fetching, updating, and clearing employees to round
 * - Managing validation, CSC, and CNEd employee configurations and hidden statuses
 * - Managing loading states for all asynchronous employee operations
 *
 * @property {object} initialState - The initial state for the employee context.
 * @property {Function} setDepts - Sets the list of departments.
 * @property {Function} setSelectedDepts - Sets the selected departments.
 * @property {Function} clearAllDepts - Clears all departments and selected departments.
 * @property {Function} getEmployeesToRound - Initiates fetching employees to round and sets loading state.
 * @property {Function} getEmployeesToRoundSuccess - Handles successful fetch of employees to round.
 * @property {Function} getEmployeesToRoundFailure - Handles failed fetch of employees to round.
 * @property {Function} clearEmployeesToRound - Clears employees to round and selected delegated employee.
 * @property {Function} getValidationSubjectsToRoundForList - Initiates fetching validation subjects to round.
 * @property {Function} getValidationSubjectsToRoundForListSuccess - Handles successful fetch of validation subjects.
 * @property {Function} getValidationSubjectsToRoundForListFailure - Handles failed fetch of validation subjects.
 * @property {Function} getCSCSubjectsToRoundForList - Initiates fetching CSC subjects to round.
 * @property {Function} getCSCSubjectsToRoundForListSuccess - Handles successful fetch of CSC subjects.
 * @property {Function} getCSCSubjectsToRoundForListFailure - Handles failed fetch of CSC subjects.
 * @property {Function} getCNEdSubjectsToRoundForList - Initiates fetching CNEd subjects to round.
 * @property {Function} getCNEdSubjectsToRoundForListSuccess - Handles successful fetch of CNEd subjects.
 * @property {Function} getCNEdSubjectsToRoundForListFailure - Handles failed fetch of CNEd subjects.
 * @property {Function} setDelegatedEmployee - Sets the selected delegated employee.
 *
 * @example
 * dispatch(setDepts(['Cardiology', 'Neurology']));
 * dispatch(setSelectedDepts(['Cardiology']));
 * dispatch(clearAllDepts());
 * dispatch(getEmployeesToRound({ hcaid: 'abc1234', directReportsOnly: true }));
 *
 * @returns {object} The employee slice reducer and actions.
 */
const employeeSlice = createSlice({
  name: 'employee',
  initialState: EmployeeContext,
  reducers: {
    setDepts: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        depts: action.payload
      };
    },
    setSelectedDepts: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        selectedDepts: action.payload
      };
    },
    clearAllDepts: state => {
      return {
        ...state,
        depts: [],
        selectedDepts: []
      };
    },
    getEmployeesToRound: (
      state,
      action: PayloadAction<IGetEmployeesToRoundPayload>
    ) => {
      return {
        ...state,
        loadingEmployeesToRound: true
      };
    },
    getEmployeesToRoundSuccess: (state, action: PayloadAction<Employee[]>) => {
      return {
        ...state,
        employeesToRound: action.payload,
        loadingEmployeesToRound: false
      };
    },
    getEmployeesToRoundFailure: state => {
      return {
        ...state,
        employeesToRound: [],
        loadingEmployeesToRound: false
      };
    },
    clearEmployeesToRound: state => {
      return {
        ...state,
        employeesToRound: [],
        selectedDelegatedEmployee: {} as IDelegateOption
      };
    },
    getValidationSubjectsToRoundForList: (
      state,
      action: PayloadAction<IGetSubjectsToRoundPayload>
    ) => {
      return {
        ...state,
        isLoadingSubjectsToRoundForList: true
      };
    },
    getValidationSubjectsToRoundForListSuccess: (
      state,
      action: PayloadAction<(Employee & RoundHistory)[]>
    ) => {
      return {
        ...state,
        validationSubjectsToRoundForList: action.payload,
        isLoadingSubjectsToRoundForList: false
      };
    },
    getValidationSubjectsToRoundForListFailure: state => {
      return {
        ...state,
        validationSubjectsToRoundForList: [],
        isLoadingSubjectsToRoundForList: false
      };
    },
    getCSCSubjectsToRoundForList: (
      state,
      action: PayloadAction<IGetSubjectsToRoundPayload>
    ) => {
      return {
        ...state,
        isLoadingSubjectsToRoundForList: true
      };
    },
    getCSCSubjectsToRoundForListSuccess: (
      state,
      action: PayloadAction<(Employee & RoundHistory)[]>
    ) => {
      return {
        ...state,
        cscSubjectsToRoundForList: action.payload,
        isLoadingSubjectsToRoundForList: false
      };
    },
    getCSCSubjectsToRoundForListFailure: state => {
      return {
        ...state,
        cscSubjectsToRoundForList: [],
        isLoadingSubjectsToRoundForList: false
      };
    },
    getCNEdSubjectsToRoundForList: (
      state,
      action: PayloadAction<IGetSubjectsToRoundPayload>
    ) => {
      return {
        ...state,
        isLoadingSubjectsToRoundForList: true
      };
    },
    getCNEdSubjectsToRoundForListSuccess: (
      state,
      action: PayloadAction<(Employee & RoundHistory)[]>
    ) => {
      return {
        ...state,
        cnedSubjectsToRoundForList: action.payload,
        isLoadingSubjectsToRoundForList: false
      };
    },
    getCNEdSubjectsToRoundForListFailure: state => {
      return {
        ...state,
        cnedSubjectsToRoundForList: [],
        isLoadingSubjectsToRoundForList: false
      };
    },
    setDelegatedEmployee: (state, action: PayloadAction<IDelegateOption>) => {
      return {
        ...state,
        selectedDelegatedEmployee: action.payload
      };
    },
    setShiftStatusData: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isShiftStatusDataAvailable: action.payload
      };
    },
    setEmployeeShiftStatusFilter: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        shiftStatusFilter: action.payload
      };
    },
    clearEmployeeShiftStatusFilter: state => {
      return {
        ...state,
        shiftStatusFilter: ''
      };
    }
  }
});

export const {
  setDepts,
  setSelectedDepts,
  clearAllDepts,
  getEmployeesToRound,
  getEmployeesToRoundSuccess,
  getEmployeesToRoundFailure,
  clearEmployeesToRound,
  getValidationSubjectsToRoundForList,
  getValidationSubjectsToRoundForListSuccess,
  getValidationSubjectsToRoundForListFailure,
  getCSCSubjectsToRoundForList,
  getCSCSubjectsToRoundForListSuccess,
  getCSCSubjectsToRoundForListFailure,
  getCNEdSubjectsToRoundForList,
  getCNEdSubjectsToRoundForListSuccess,
  getCNEdSubjectsToRoundForListFailure,
  setDelegatedEmployee,
  setShiftStatusData,
  setEmployeeShiftStatusFilter,
  clearEmployeeShiftStatusFilter
} = employeeSlice.actions;

export default employeeSlice.reducer;
